// Columns of the expense report list (XPEN-3788). `id` is the API sort and filter key;
// a choice column lists the values its filter offers.
const EXPENSE_REPORT_STATUSES = [
  'Pending', 'Submitted', 'Need_Approval', 'Pending_Exception_Approval', 'Validated', 'Approved', 'Denied', 'Finalized',
].map((status) => ({ value: status, label: `expense_account_status.${status.toLowerCase()}` }));

// XPEN-3866: the API counts any other payment status as not paid, like a report without one,
// while the cell keeps their own pill: "Non payée" also lists rows shown "En cours" or "Refusée".
const PAYMENT_STATES = [
  { value: 'Not_Paid', label: 'invoice.payment_state_not_paid' },
  { value: 'Partially_Paid', label: 'invoice.payment_state_partial' },
  { value: 'Processed', label: 'invoice.payment_state_paid' },
];

const formatDateTime = (value, data) => (value?.$date
  ? Intl.DateTimeFormat(`${data.t.lang}-US`, {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(new Date(value.$date))
  : '');

// XPEN-3866: the compact list has no payment column, so once the ERP has paid the report its
// payment follows the status there. A report not yet paid shows its status alone.
export const reportPaymentLabel = (report, data) => {
  const payment = report.payment || {};
  if (payment.status === 'Processed') {
    const date = payment.CheckDate ? new Date(payment.CheckDate) : null;
    return date && !Number.isNaN(date.getTime())
      ? data.t.read('expense_account_payment.paid_on').replace('{date}', Intl.DateTimeFormat(`${data.t.lang}-US`, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date))
      : data.t.read('expense_account_payment.paid');
  }
  if (payment.status === 'Partially_Paid') return data.t.read('expense_account_payment.partially_paid');
  return '';
};

const statusLabel = (report, data) => (report.status ? data.t.read(`expense_account_status.${report.status.toLowerCase()}`) : '');

export const reportStatusLabel = (report, data) => {
  const status = statusLabel(report, data);
  if (!status) return '';
  const payment = reportPaymentLabel(report, data);
  return payment ? `${status} · ${payment}` : status;
};

const formatAmount = (value) => (value ? `$${value.toFixed(2)}` : '');

export const EXPENSE_REPORT_COLUMNS = [
  { id: 'description', label: 'invoice.description', filter: 'text', evaluate: (report) => report.description || '' },
  { id: 'report_number', label: 'invoice.report_number', filter: 'text', evaluate: (report) => report.report_number || '' },
  // XPEN-3791: the approver a pending report awaits, or who approved or denied it; and the
  // approver it was assigned to, who differs when someone else decided on their behalf.
  { id: 'approver', label: 'approver', filter: 'text', evaluate: (report) => report.approver || '' },
  { id: 'assigned_approver', label: 'assigned_approver', filter: 'text', evaluate: (report) => report.assigned_approver || '' },
  { id: 'created_at', label: 'invoice.created_at', filter: 'date', evaluate: (report, data) => formatDateTime(report.created_at, data) },
  { id: 'updated_at', label: 'invoice.updated_at', filter: 'date', evaluate: (report, data) => formatDateTime(report.updated_at, data) },
  { id: 'submitted_at', label: 'invoice.submitted_at', filter: 'date', evaluate: (report, data) => formatDateTime(report.submitted_at, data) },
  { id: 'currency', label: 'invoice.currency', filter: 'text', evaluate: (report) => report.currency || '' },
  {
    id: 'status',
    label: 'invoice.status',
    filter: 'choice',
    choices: EXPENSE_REPORT_STATUSES,
    evaluate: (report, data) => statusLabel(report, data),
  },
  { id: 'Total', label: 'invoice.total', filter: 'number', evaluate: (report) => formatAmount(report.Total) },
  { id: 'BigTotalPayed', label: 'invoice.total_with_tax', filter: 'number', evaluate: (report) => formatAmount(report.BigTotalPayed) },
  // XPEN-3866: drawn by the invoice list's payment cell. Sorted not paid, partly paid, then paid by payment date.
  {
    id: 'payment', label: 'invoice.payment_status', filter: 'choice', choices: PAYMENT_STATES, evaluate: (report) => report.payment?.status || '',
  },
];

const STORAGE_KEY = 'expensesAccountsColumns';
const DEFAULT_VISIBLE = EXPENSE_REPORT_COLUMNS.map((column) => column.id);
const KNOWN = new Set(DEFAULT_VISIBLE);

/** The columns the person chose, in table order; every column when nothing valid was saved. */
export const loadVisibleColumns = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    const valid = Array.isArray(saved) ? saved.filter((id) => KNOWN.has(id)) : [];
    return valid.length ? DEFAULT_VISIBLE.filter((id) => valid.includes(id)) : DEFAULT_VISIBLE;
  } catch {
    return DEFAULT_VISIBLE;
  }
};

export const saveVisibleColumns = (ids) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // A blocked storage only loses the choice for the next visit.
  }
};

const isEmptyRange = (value) => !Array.isArray(value) || value.every((bound) => bound === '' || bound == null);

/** True when a filter value would select something; empty ones are dropped rather than sent. */
export const isActiveFilter = (column, value) => {
  if (column.filter === 'text') return typeof value === 'string' && value.trim() !== '';
  if (column.filter === 'choice') return Array.isArray(value) && value.length > 0;
  return !isEmptyRange(value);
};

/** One sort at a time: ascending, then descending, then back to the default order. */
export const nextSorting = (sorting, id) => {
  const current = sorting[0];
  if (!current || current.id !== id) return [{ id, desc: false }];
  return current.desc ? [] : [{ id, desc: true }];
};

/** The query string of GET /expenses_accounts/. */
export const buildListQuery = ({ page, pageSize, filters, sorting, associateId }) => {
  const sent = Object.entries(filters).map(([id, value]) => ({ id, value }));
  if (associateId) sent.push({ id: 'user.id_user', value: associateId });
  const params = new URLSearchParams({
    start: String(page * pageSize),
    page_size: String(pageSize),
    filters: JSON.stringify(sent),
    sorting: JSON.stringify(sorting),
  });
  return `?${params.toString()}`;
};
